import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { SlideOverPanel } from '@/shared/ui';

import { SourcePanelContent } from './SourcePanelContent';
import type { Source } from './sourcePanel.types';

interface SourcePanelProps {
  /** The record to describe, or `null` when the panel is closed. */
  source: Source | null;
  onClose: () => void;
}

/**
 * The source drawer: a citation's record, shown beside the sentence that cited it.
 *
 * Presentational: the page passes what to show. Which citation is selected lives in
 * `SourcePanelContext`, because the badge that sets it renders far below this in the tree.
 */
export const SourcePanel = ({ source, onClose }: SourcePanelProps) => {
  const { t } = useTranslation('analysis');
  const open = source !== null;

  /* The closing panel still has to paint something while it slides shut, so it keeps drawing
     the source it was last opened with. Never read when open. */
  const lastSource = useRef<Source | null>(null);
  if (source) lastSource.current = source;
  const shown = source ?? lastSource.current;

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      label={t('analysis.sourcePanel.title')}
      closeLabel={t('analysis.sourcePanel.close')}
      title={t('analysis.sourcePanel.title')}
      testId="source-panel"
    >
      {shown && <SourcePanelContent source={shown} />}
    </SlideOverPanel>
  );
};

SourcePanel.displayName = 'SourcePanel';
